import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useGlobalConfig } from 'context/GlobalContext';
import { PilotApis } from 'apis/pilotAPI';
import { Environment } from 'types/apis/pilotInterfaces';
import { BglPrimaryButton, BglPrimaryOutlinedButton } from 'components/button/BglButtons';
import { CreateEnvironmentDialog } from './CreateEnvironmentDialog';
import { EditEnvironmentDialog } from './EditEnvironmentDialog';
import { useToast } from 'context/ToastContext';

export const EnvironmentListPage = () => {
  const { rc } = useGlobalConfig();
  const toast = useToast();
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEnvironment, setEditingEnvironment] = useState<Environment | null>(null);

  // Fetch all environments on component mount
  useEffect(() => {
    fetchEnvironments();
  }, []);

  /**
   * Maps /v1/environments API response to Environment type.
   */
  const mapApiEnvToEnvironment = (env: any): Environment => {
    const utility = env.utility;
    return {
      id: env.id,
      environment_name: env.environment_name ?? env.name ?? '',
      status: env.status ?? 'active',
      stack_name: env.stack_name ?? env.stackName ?? '',
      utility_id: env.utility_id ?? utility?.id ?? 0,
      utility: utility
        ? {
            id: utility.id,
            name: utility.name ?? '—',
            created_at: utility.created_at != null ? String(utility.created_at) : '',
            updated_at: utility.updated_at != null ? String(utility.updated_at) : '',
          }
        : {
            id: 0,
            name: '—',
            created_at: '',
            updated_at: '',
          },
      process_id: env.process_id ?? '',
      process_name: env.process_name ?? '',
      process_type: env.process_type ?? '',
      task_id: env.task_id ?? '',
      created_at: env.created_at != null ? String(env.created_at) : '',
      updated_at: env.updated_at != null ? String(env.updated_at) : '',
      process_data: env.process_data ?? {},
      serverUrl: env.data_server_url ?? env.serverUrl ?? undefined,
      frontend_url: env.frontend_url ?? undefined,
      aws_region: env.aws_region ?? undefined,
    };
  };

  const getFetchErrorMessage = (responseError: any): string => {
    const msg = typeof responseError === 'string' ? responseError : responseError?.message ?? '';
    const code = typeof responseError === 'object' ? responseError?.code : undefined;
    const isNetworkError =
      code === 'ERR_NETWORK' ||
      (typeof msg === 'string' && msg.toLowerCase().includes('network error'));
    const apiUrl = process.env.REACT_APP_TOOL_API_URL || 'http://localhost:5000';
    if (isNetworkError) {
      return `Unable to reach the server at ${apiUrl}. Ensure the backend (e.g. proxypulse) is running and that REACT_APP_TOOL_API_URL is correct.`;
    }
    return msg || 'Failed to fetch environments';
  };

  const fetchEnvironments = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // DB-only list (no external env-manager call)
      const response = await PilotApis.getAllEnvironments(rc);

      if (response.error) {
        setError(getFetchErrorMessage(response.error));
        setEnvironments([]);
        setTotalCount(0);
        return;
      }

      const rawData = response.data?.data ?? [];
      const count = response.data?.count ?? rawData.length;
      const mapped = Array.isArray(rawData) ? rawData.map(mapApiEnvToEnvironment) : [];
      setEnvironments(mapped);
      setTotalCount(typeof count === 'number' ? count : mapped.length);
    } catch (err) {
      setError('An error occurred while fetching environments');
      setEnvironments([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewEnvironment = () => {
    setCreateDialogOpen(true);
  };

  const handleCreateEnvironmentSuccess = () => {
    toast.success('Environment created successfully.');
    fetchEnvironments();
  };

  const handleExportJSON = () => {
    try {
      const dataStr = JSON.stringify(environments, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'environments.json';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting JSON:', err);
    }
  };

  const handleViewEnvironment = (environment: Environment) => {
    // TODO: Implement view environment details
    console.log('View environment:', environment);
  };

  const handleEditEnvironment = (environment: Environment) => {
    setEditingEnvironment(environment);
    setEditDialogOpen(true);
  };

  const handleEditEnvironmentSuccess = () => {
    toast.success('Environment updated successfully.');
    setEditingEnvironment(null);
    setEditDialogOpen(false);
    fetchEnvironments();
  };

  const handleDeleteEnvironment = (environment: Environment) => {
    // TODO: Implement delete environment with confirmation
    console.log('Delete environment:', environment);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'completed':
        return 'success';
      case 'inactive':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={fetchEnvironments} variant="contained">
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Environments Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden', boxShadow: 2, borderRadius: 2, p: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center',justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LinkIcon sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
            <Typography variant="h4" component="h1" sx={{ 
              fontWeight: 700, 
              color: 'text.primary',
              flex: 1
            }}>
              Environments
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <BglPrimaryOutlinedButton
              onClick={handleExportJSON}
              startIcon={<DownloadIcon />}
            >
              Export JSON
            </BglPrimaryOutlinedButton>
            <BglPrimaryButton
              onClick={handleNewEnvironment}
              startIcon={<AddIcon />}
            >
              New Environment
            </BglPrimaryButton>
          </Box>
        </Box>
        <Typography variant="body1" sx={{ 
          color: 'text.secondary',
          fontSize: '1rem',
          textAlign: 'left'
        }}>
          Manage and view different deployment environments. {totalCount > 0 && `Total: ${totalCount} environments`}
        </Typography>
      </Box>

        <Box>
        <TableContainer style={{border: '1px solid rgb(222, 222, 222)',borderRadius: '6px'}}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  backgroundColor: 'background.paper',
                  borderBottom: '2px solid',
                  borderColor: 'divider'
                }}>
                  Name
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  backgroundColor: 'background.paper',
                  borderBottom: '2px solid',
                  borderColor: 'divider'
                }}>
                  Utility
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  backgroundColor: 'background.paper',
                  borderBottom: '2px solid',
                  borderColor: 'divider'
                }}>
                  Stack Name
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  backgroundColor: 'background.paper',
                  borderBottom: '2px solid',
                  borderColor: 'divider'
                }}>
                  Data Server URL
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  backgroundColor: 'background.paper',
                  borderBottom: '2px solid',
                  borderColor: 'divider'
                }}>
                  Frontend URL
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  backgroundColor: 'background.paper',
                  borderBottom: '2px solid',
                  borderColor: 'divider'
                }}>
                  Process Type
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  backgroundColor: 'background.paper',
                  borderBottom: '2px solid',
                  borderColor: 'divider'
                }}>
                  Status
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  backgroundColor: 'background.paper',
                  borderBottom: '2px solid',
                  borderColor: 'divider'
                }}>
                  Created
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  backgroundColor: 'background.paper',
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  textAlign: 'center'
                }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {environments.map((environment) => (
                <TableRow key={environment.id} hover>
                  <TableCell sx={{ fontWeight: 500, color: 'text.primary' }}>
                    {environment.environment_name}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {environment.utility?.name || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {environment.stack_name || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: 'text.secondary', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                      {(environment.serverUrl ?? `https://api-server-${environment.stack_name}-uat.bidgely.com`) || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: 'text.secondary', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                      {environment.frontend_url || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {environment.process_type || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={environment.status} 
                      color={getStatusColor(environment.status)}
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {environment.created_at ? new Date(environment.created_at).toLocaleDateString() : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                      {/* <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewEnvironment(environment)}
                          sx={{ color: 'info.main' }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip> */}
                      <Tooltip title="Edit Environment">
                        <IconButton
                          size="small"
                          onClick={() => handleEditEnvironment(environment)}
                          sx={{ color: 'primary.main' }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {/* <Tooltip title="Delete Environment">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteEnvironment(environment)}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip> */}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <CreateEnvironmentDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onSuccess={handleCreateEnvironmentSuccess}
        />

        <EditEnvironmentDialog
          open={editDialogOpen}
          environment={editingEnvironment}
          onClose={() => {
            setEditDialogOpen(false);
            setEditingEnvironment(null);
          }}
          onSuccess={handleEditEnvironmentSuccess}
        />

        {environments.length === 0 && (
          <Box sx={{ 
            p: 4, 
            textAlign: 'center',
            color: 'text.secondary'
          }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              No environments defined yet. Click &quot;New Environment&quot; to create one.
            </Typography>
            <BglPrimaryButton onClick={handleNewEnvironment} startIcon={<AddIcon />}>
              New Environment
            </BglPrimaryButton>
          </Box>
        )}
        </Box>
      </Paper>
    </Box>
  );
};